/**
 * Real players as family and partners: you ask another player to be your sister (or your girlfriend, your mother…),
 * they say yes on their screen, and each life records the other the right way round (your mother has you as her
 * son or daughter). While a player holds a role, the game's own character in it steps aside (out of your contacts:
 * no texts, no calls, no visits), and comes back if that link ever ends. A partner is one at a time: a player partner
 * means the game's girlfriend or boyfriend steps aside too; married to (or engaged to) one of the game's, no.
 */
import type { LifeState } from './life.ts';

/**
 * The game's own family and partners (a generated mother, father, siblings, a girlfriend or a husband, children; NPC
 * dates, asking an NPC out, proposing to one, a baby with one): ARCHIVED (2026-10-28, players asked for real people
 * only). The code stays as it was, switched off here, so it can come back: nothing is generated, nobody NPC becomes
 * family or a partner, and a life that had them keeps them in `archive.kin`, out of sight (archiveNpcKin). Family and
 * partners are real players now (below), or no one: there is no NPC to fall back on.
 */
export const NPC_KIN = false;
/**
 * The game's own people in your contacts at all (friends, neighbours, a shop owner, the people from an event or a
 * gala): ARCHIVED too (2026-10-28, the user: Contacts is real players). Kept in `archive.contacts`, out of sight;
 * whatever adds one now goes there at once (archiveNpcContacts, every tick). Players are s.players, untouched.
 */
export const NPC_CONTACTS = false;
export function archiveNpcContacts(s: LifeState): void {
  if (NPC_CONTACTS || !s.contacts.length) return;
  const a = ((s as LifeState & { archive?: { contacts?: LifeState['contacts'] } }).archive ??= {});
  (a.contacts ??= []).push(...s.contacts.filter((c) => !a.contacts!.some((x) => x.name === c.name)));
  s.contacts = [];
}
/** the relations that are family or romance (an NPC in one is archived) */
export const KIN_RELATIONS = ['Mother', 'Father', 'Brother', 'Sister', 'Son', 'Daughter', 'Cousin', 'Girlfriend', 'Boyfriend', 'Partner', 'Wife', 'Husband', 'Fiancée', 'Fiancé', 'Ex'];
type Archive = { kin?: LifeState['contacts']; engaged?: unknown; pregnancy?: unknown; aside?: unknown[]; dates?: unknown[]; at?: number };
/**
 * A life from before: its game-made family and partners into the archive (kept whole: their closeness, when you met,
 * an engagement to one, a pregnancy, NPCs who'd stepped aside for a player, dates booked with them). Once; again does
 * nothing. Returns how many people went.
 */
export function archiveNpcKin(s: LifeState): number {
  if (NPC_KIN) return 0;
  const k = s as LifeState & { archive?: Archive; aside?: { c: LifeState['contacts'][number]; for: string }[]; dates?: { with?: string; state?: string }[] };
  const a: Archive = (k.archive ??= {});
  const gone = s.contacts.filter((c) => KIN_RELATIONS.includes(c.relation));
  if (gone.length) { (a.kin ??= []).push(...gone); s.contacts = s.contacts.filter((c) => !gone.includes(c)); }
  if (s.engaged && !(s.engaged as { player?: string }).player) { a.engaged = s.engaged; s.engaged = null; }
  if (s.pregnancy) { a.pregnancy = s.pregnancy; s.pregnancy = null; }
  if (k.aside?.length) { (a.aside ??= []).push(...k.aside); k.aside = []; }
  const npcDates = (k.dates ?? []).filter((d) => d.with !== 'player' && (d.state === 'booked' || d.state === 'invited'));
  if (npcDates.length) { (a.dates ??= []).push(...npcDates); for (const d of npcDates) d.state = 'declined'; }
  if (gone.length || a.engaged || a.pregnancy) a.at ??= s.minutes;
  return gone.length;
}

/** what another player can be to you (a wife or a husband, not "partner": players found it confusing. Partner stays
 *  for the ones already asked) */
export const PLAYER_ROLES = ['Girlfriend', 'Boyfriend', 'Wife', 'Husband', 'Sister', 'Brother', 'Mother', 'Father', 'Son', 'Daughter', 'Cousin', 'Best friend'] as const;
export type PlayerRole = typeof PLAYER_ROLES[number] | 'Partner' | 'Fiancée' | 'Fiancé';
const PARTNER = ['Girlfriend', 'Boyfriend', 'Partner', 'Wife', 'Husband', 'Fiancée', 'Fiancé'];
export const isPartnerRole = (r: string | undefined) => !!r && PARTNER.includes(r);
const isRole = (r: unknown): r is PlayerRole => typeof r === 'string' && ([...PLAYER_ROLES, 'Partner', 'Fiancée', 'Fiancé'] as string[]).includes(r);
export const validRole = isRole;

/** what you are to them, when they're `role` to you (by your sex: your sister has you as her brother or sister) */
export function reciprocal(role: PlayerRole, mySex: 'm' | 'f'): PlayerRole {
  const m = mySex === 'm';
  switch (role) {
    case 'Girlfriend': case 'Boyfriend': return m ? 'Boyfriend' : 'Girlfriend';
    case 'Wife': case 'Husband': return m ? 'Husband' : 'Wife';
    case 'Fiancée': case 'Fiancé': return m ? 'Fiancé' : 'Fiancée';
    case 'Sister': case 'Brother': return m ? 'Brother' : 'Sister';
    case 'Mother': case 'Father': return m ? 'Son' : 'Daughter';
    case 'Son': case 'Daughter': return m ? 'Father' : 'Mother';
    default: return role; // (Partner, Cousin, Best friend: the same both ways)
  }
}

type Contact = LifeState['contacts'][number];
type Kin = LifeState & { aside?: { c: Contact; for: string }[] };

/** the game's character in this role now (a partner: whoever you're seeing; else the same relation) */
export function npcIn(s: LifeState, role: PlayerRole): Contact | undefined {
  if (!NPC_KIN) return undefined; // (archived: there's no game-made one to step aside)
  return isPartnerRole(role) ? s.contacts.find((c) => ['Girlfriend', 'Boyfriend', 'Partner'].includes(c.relation)) : s.contacts.find((c) => c.relation === role);
}

/** why you can't have a player as this (null: you can) */
export function roleBlocked(s: LifeState, role: PlayerRole, playerId: string): string | null {
  if (!isPartnerRole(role)) return null;
  const wed = s.contacts.find((c) => c.relation === 'Wife' || c.relation === 'Husband');
  if (wed) return `You're married to ${wed.name}.`;
  if (s.engaged && s.contacts.some((c) => c.name === s.engaged!.name)) return `You're engaged to ${s.engaged.name}.`;
  const other = (s.players ?? []).find((p) => p.id !== playerId && isPartnerRole(p.relation));
  if (other) return `You're already with ${other.name}.`;
  return null;
}

/** a player is now `role` to you: the game's character in it steps aside (kept, for if this ends). The character's name, if one did */
export function takeRole(s: LifeState, player: { id: string; name: string }, role: PlayerRole): string | null {
  const k = s as Kin, pl = (s.players ??= []).find((p) => p.id === player.id);
  if (pl) pl.relation = role; else s.players.push({ id: player.id, name: player.name, met: s.minutes, relation: role });
  const npc = NPC_KIN ? npcIn(s, role) : undefined;
  if (npc) { s.contacts.splice(s.contacts.indexOf(npc), 1); (k.aside ??= []).push({ c: npc, for: player.id }); }
  s.history.push({ t: s.minutes, text: isPartnerRole(role) ? `Started seeing ${player.name}.` : `${player.name} became your ${role.toLowerCase()}.` });
  return npc?.name ?? null;
}

/** that link's over: the player's just someone you know again, and whoever stepped aside for them is back */
export function endRole(s: LifeState, playerId: string): string[] {
  const k = s as Kin, pl = s.players?.find((p) => p.id === playerId);
  if (pl) { if (isPartnerRole(pl.relation)) s.history.push({ t: s.minutes, text: `Broke up with ${pl.name}.` }); delete pl.relation; }
  if (s.engaged?.player === playerId) s.engaged = null; // (broken up: the engagement's off too)
  moveOut(s, playerId); // (and living together)
  const back = (k.aside ?? []).filter((a) => a.for === playerId);
  k.aside = (k.aside ?? []).filter((a) => a.for !== playerId);
  if (NPC_KIN) for (const a of back) if (!s.contacts.some((c) => c.name === a.c.name)) s.contacts.push(a.c); // (archived: nobody comes back in their place)
  return back.map((a) => a.c.name);
}

/** the player who's `role` to you (the first) */
export const playerIn = (s: LifeState, role: PlayerRole | 'partner') => (s.players ?? []).find((p) => (role === 'partner' ? isPartnerRole(p.relation) : p.relation === role));

// --- living together (married players) --------------------------------------------------------------------------

/** a house as it travels between players: which, where, and how it's done up */
export interface SharedHouse { id: string; label: string; area: string; style: string }
type Home2 = LifeState & { livesWith?: { id: string; name: string; home: SharedHouse } | null; housemate?: { id: string; name: string } | null };
/** whose house you live in (your spouse's), if you've moved in */
export const livesWith = (s: LifeState) => (s as Home2).livesWith ?? null;
/** your spouse who's moved in with you */
export const housemate = (s: LifeState) => (s as Home2).housemate ?? null;
export const isSpouse = (s: LifeState, id: string) => ['Wife', 'Husband'].includes((s.players ?? []).find((p) => p.id === id)?.relation ?? '');
/** you've moved into their house (your own place stays yours) */
export function moveInWith(s: LifeState, owner: { id: string; name: string }, home: SharedHouse) {
  (s as Home2).livesWith = { id: owner.id, name: owner.name, home };
  s.history.push({ t: s.minutes, text: `Moved in with ${owner.name}.` });
}
/** they've moved into yours */
export function tookIn(s: LifeState, who: { id: string; name: string }) { (s as Home2).housemate = { id: who.id, name: who.name }; }
/** the owner did the house up again: the mover's copy of it */
export function houseRedone(s: LifeState, owner: string, home: SharedHouse) { const l = (s as Home2).livesWith; if (l?.id === owner) l.home = home; }
/** living apart again (either side) */
export function moveOut(s: LifeState, playerId: string) {
  const k = s as Home2;
  if (k.livesWith?.id === playerId) { s.history.push({ t: s.minutes, text: `Moved out of ${k.livesWith.name}'s.` }); k.livesWith = null; }
  if (k.housemate?.id === playerId) k.housemate = null;
}
